import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Trash2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";

const ACTION = {
  cancel: { label: "Cancelled", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", Icon: Ban },
  delete: { label: "Deleted", cls: "text-red-300 border-red-500/40 bg-red-500/10", Icon: Trash2 },
  restore: { label: "Restored", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", Icon: RotateCcw },
};

export default function DeletedLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/admin/deleted-log").then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (user && !isAdmin) return <Navigate to="/" replace />;

  const restore = async (id) => {
    try {
      await api.post(`/referrals/${id}/restore`);
      toast.success(`${id} restored`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="deleted-log-page">
      <div>
        <h1 className="font-head font-black text-3xl tracking-tight">Recently Cancelled &amp; Deleted</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">{rows.length} events · from the tamper-evident audit trail · cancelled referrals can be restored</p>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground font-mono border-b border-white/8">
              <th className="text-left px-3 py-2.5">When</th>
              <th className="text-left px-2 py-2.5">Event</th>
              <th className="text-left px-2 py-2.5">Referral</th>
              <th className="text-left px-2 py-2.5">By</th>
              <th className="text-left px-2 py-2.5">Reason / Note</th>
              <th className="text-right px-3 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {loaded && rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground font-mono text-xs">No cancellations or deletions recorded.</td></tr>
            )}
            {rows.map((e, i) => {
              const meta = ACTION[e.action] || { label: e.action, cls: "text-muted-foreground border-white/15", Icon: Ban };
              const restorable = e.action === "cancel" && e.current?.exists && e.current?.cancelled;
              const gone = !e.current?.exists;
              return (
                <tr key={i} className="row-hover border-b border-white/5 last:border-0" data-testid={`deleted-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(e.at)}</td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-mono ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    {gone
                      ? <span className="font-mono text-xs text-muted-foreground">{e.referral_id} <span className="text-red-400/70">(purged)</span></span>
                      : <button onClick={() => navigate(`/referrals/${e.referral_id}`)} data-testid={`deleted-ref-${e.referral_id}`} className="font-mono text-cyan-300 text-xs hover:underline">{e.referral_id}</button>}
                  </td>
                  <td className="px-2 py-2 text-xs whitespace-nowrap">{e.user_name} <span className="text-muted-foreground">({e.role})</span></td>
                  <td className="px-2 py-2 text-xs font-mono text-slate-400">{e.note || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {restorable
                      ? <button onClick={() => restore(e.referral_id)} data-testid={`restore-${e.referral_id}`} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2.5 py-1 text-xs font-mono hover:bg-emerald-500/20 transition-colors"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>
                      : gone ? <span className="text-[11px] font-mono text-muted-foreground">permanent</span>
                      : <span className="text-[11px] font-mono text-muted-foreground">{e.current?.cancelled ? "cancelled" : "active"}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
